import { SvgIconProps, Theme as UITheme } from '@teko/ui';
import { NetworkConfig } from '@teko/loyalty';
import { JSONObject } from '../types';
import { LocationOption } from 'server/internal-services/public-bff.interface';

interface Logo {
  path: string;
  height?: number;
  width?: number;
}
interface TenantLogo {
  full: Logo;
  lite: Logo;
  mobile: Logo;
  loading: Logo;
}

interface TerminalLocation {
  ward: string;
  district: string;
  province: string;
}
export interface Terminal {
  id: number;
  code: string;
  label: string;
  provinceCodes?: string[];
  locationCode?: string;
  isDefault?: boolean;
  location?: TerminalLocation;
}
interface Location {
  provinces?: string[];
  districts?: string[];
  wards?: string[];
}

interface MobileAppInfo {
  appLink: string;
  appName: string;
}

export interface SiteMeta {
  title: string;
  description: string;
  image: string;
}

interface TenantCustomInfo {
  icon: SvgIconProps;
  title: string;
  href?: string;
  regionName?: string;
}

interface PriceFilter {
  max: number;
  min: number;
  step?: number;
}

export enum LocationMessageType {
  'None' = '0',
  'Hint' = '1',
  'Error' = '2',
}

export interface TenantConfig {
  terminal: Terminal[] | string;
  terminalId: number;
  terminalCode_in?: string;

  /* @deprecated */
  channel?: string;
  /* @deprecated */
  channelId?: number;
  /* @deprecated */
  channelType?: string;
  /* @deprecated */
  channelCode_in?: string;

  logoSize?: number;
  supportedPhone?: string;
  logo: TenantLogo;
  isDisplayMessenger?: boolean;
  contactLink?: string;
  zaloContactLink?: string;
  notificationAppId?: string;
  allowCommentAndReceiveReply?: boolean;
  salesAndPrivacyPolicyHref?: SalesAndPrivacyPolicyHref;
  topBannerZone?: string;
  locationsLimit?: Location;
  theme: Partial<UITheme>;
  scopes?: string[];
  sellerId?: number; // phongvu
  clientConsultantPhone?: string; //phongvu
  businessCustomerPhone?: string; //phongvu
  showrooms?: boolean; //phongvu
  mobileAppInfo?: MobileAppInfo;
  allowAddToCartFromListing?: boolean;

  // data getting from seller gateway
  dealerCanEditAddress?: boolean;

  // site meta data like logo, title ...
  meta: SiteMeta;

  // loyalty
  loyaltyClientId?: string;
  changePriceFeature?: boolean; //umbala
  loyaltyExternal?: boolean;
  loyaltyNetworkConfig?: NetworkConfig;

  // customer can create return request
  returnRequestFeature?: boolean;

  // require user to identity infomation (dob, sex, name) before interacting with website
  identityRequired?: 'dealer-required' | 'dealer-not-required';

  // require user to login before interact with website
  // true: umbala, karavan
  // false: others
  requireLogin?: boolean;

  platformId?: number;

  customInfo?: TenantCustomInfo[]; // custom info which is displayed on Account page for tenant

  // Payment type: 'normal' | 'bank' | 'terra' | 'd365'
  // normal -> get payment method list from ps
  // bank -> ibanking
  // terra -> vnpaymerchant (karavan)
  // d365 -> do not payment, only create order (meatdeli)
  paymentType: string;
  hasNews?: boolean;
  notEditCustomerType?: boolean; // flag check can edit customer type with dealer cp
  availableLanguages?: string[];
  // Threshold that product is free shipped
  // default is 300000 (800000 for phongvu desktop) (this can be changed)
  // negative for disable this feature
  freeShippingThreshold?: number;
  hideAccountInfo?: boolean;

  //@TODO: replace by enum
  template?: string;

  needConfirmMembershipAgreement?: boolean;

  // value to hidden delivery information section on showroom checkout screen
  hideCheckoutdeliveryInfomation?: boolean;

  // config boolean check override footer
  noOverride?: boolean;

  // config for footer data
  footerDataConfig?: JSONObject;

  // config for vietin footer data
  vietinFooterDataConfig?: JSONObject;

  // config for top bar button
  rightMenuTopBarButtonConfigs?: JSONObject[];
  leftMenuTopBarButtonConfigs?: JSONObject[];

  // ECOM-296 questionnaires survey url
  questionnairesUrl?: string;

  // ECOM-296 configure the number of times the user visits the site without answering the questionnaries
  askAgainQuestionnairesConfig?: number;

  // ECOM-297 iamClientId of new website is obtained from Get websites list API
  iamClientId?: string;

  // ECOM-297 paymentSecretKey of new website is obtained from Get websites list API
  paymentSecretKey?: string;

  // ECOM-297 paymentTerminalCode of new website is obtained from Get websites list API
  paymentTerminalCode?: string;

  // ECOM-297 iamOauthDomain of new website is obtained from Get websites list API
  iamOauthDomain?: string;

  // ECOM-359 websiteId to get area for new website
  websiteId?: number;

  // ECOM-368 support decimal quantity
  uomCodeToDisplayDecimalQuantity?: string;

  requiresUserOverEighteenYearsOld?: boolean;

  withoutPayment?: boolean;

  logoUrl?: string;

  priceFilter?: PriceFilter;

  // is email required in delivery information
  deliveryRequireEmail?: boolean;

  // ECOM-411: do not show popup for bot, which caused large area paint
  isBot?: boolean;

  serviceWorkerScripts?: Array<string>;

  // tracking app Id
  trackingId?: string;

  // flag for PB-1887 and replace for allowRenderCustomPage (ECOM-424)
  epicPb1887?: boolean;

  smartActivationConfig?: {
    appId?: string;
    sessionPeriodMinutes?: number;
  };

  // ECOM-284: to map terminalId from desktop terminalId
  // Website management only store desktop terminalId
  mobileTerminalIdMapping?: {
    [key: string]: { terminalId: number; terminalCode: string };
  };

  location?: string;

  locations?: LocationOption[];

  epicOmni964?: boolean;

  // only use in UX4BUYER-5123.
  // Todo: alter messageLink by contactLink when pushdy off in soibien.vn
  messengerLink?: string;
}

// this class is for backward compatible
// with existing code
// there is no particular reason why certain attributes
// reside in TenantEnv not TenantConfig
// historically TenantEnv is an interface that is used
// in many places.
export interface TenantEnv extends TenantConfig {
  terminal: string;
  tenant: string;
  originalTerminal?: string | Terminal[];
  getProcessEnv: (variable: string) => string;
  isMobile: boolean;
  partner?: string;
  baseDomain?: string;
  location?: string;
  megaMenuType: 'horizontal' | 'vertical';
  alignMegaMenu: 'left' | 'right' | 'space-between';
  filterType: 'horizontal' | 'vertical';
  hiddenBottomBar?: boolean;
  mobioDomainId?: string;
  currentLanguage?: string;

  // task UX4BUYER-3637 wants keep old url for SEO. This variable contains mapping bettween old url with new url
  updatedUrlMap?: Iterable<readonly [string, string]>;

  // task CSFRONT-945 support to temporarily close the website for karavan
  temporarilyClosePlatform?: boolean;
}

export interface Area {
  id: string;
  content: string;
}

export interface SalesAndPrivacyPolicyHref {
  salesPolicy: string;
  privacyPolicy: string;
}
